import React, { useState, useEffect, useMemo, useRef } from "react";
import ContactVendorleadsModal from "../../components/ContactVendorleadsModal";
import SignInModal from "../../components/SignInModal";
import Navbar from "../../components/Navbar";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT_COLOR = "#10b981";
const BRAND_BLUE = "#3b82f6";
const GRAY_DARK_COLOR = "#374151";

/* Stroke-icon set (matches Landing.js visual system) */
const ICON_PATHS = {
  search: (<><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>),
  sliders: (<><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></>),
  upload: (<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>),
  image: (<><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></>),
  edit: (<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" /></>),
  bell: (<><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></>),
  eye: (<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>),
  x: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
  check: <polyline points="20 6 9 17 4 12" />,
  checkCircle: (<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>),
  home: (<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>),
  bed: (<><path d="M2 4v16" /><path d="M2 8h18a2 2 0 0 1 2 2v10" /><path d="M2 17h20" /><path d="M6 8v9" /></>),
  bath: (<><path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" /><line x1="10" y1="5" x2="8" y2="7" /><line x1="2" y1="12" x2="22" y2="12" /><line x1="7" y1="19" x2="7" y2="21" /><line x1="17" y1="19" x2="17" y2="21" /></>),
  car: (<><path d="M5 17h14M5 17l-1.5-5h17L19 17M5 17v3M19 17v3" /><circle cx="7.5" cy="17.5" r="1.5" /><circle cx="16.5" cy="17.5" r="1.5" /></>),
  ruler: (<><path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" /></>),
  chart: (<><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></>),
  send: (<><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></>),
  cpu: (<><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><line x1="9" y1="1" x2="9" y2="4" /><line x1="15" y1="1" x2="15" y2="4" /><line x1="9" y1="20" x2="9" y2="23" /><line x1="15" y1="20" x2="15" y2="23" /><line x1="20" y1="9" x2="23" y2="9" /><line x1="20" y1="14" x2="23" y2="14" /><line x1="1" y1="9" x2="4" y2="9" /><line x1="1" y1="14" x2="4" y2="14" /></>),
  brand: (<><circle cx="12" cy="8" r="7" /><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" /></>),
  message: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />,
  activity: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
};

function Icon({ name, size = 20, color = "currentColor", strokeWidth = 2, style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

function IconTile({ name, size = 38, iconSize = 18, color = BRAND_ACCENT_COLOR, style }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        background: `${color}1a`,
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        ...style,
      }}
    >
      <Icon name={name} size={iconSize} color={color} />
    </div>
  );
}

/* ─────────── Demo Agency (mirrors QRCodeFlyer) ─────────── */
const DEMO_AGENCY = {
  agency_name: "Demo Agency",
  agent_name: "John Doe",
  agent_first_name: "John",
  agent_title: "Licensed Agent",
  agent_email: "bayan+demo@vendorleads.com.au",
  agent_phone: "0400 000 000",
  agent_colour: "#000000",
  landing_page_slug: "demo-agency",
  logo_url:
    "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/agencies/demo-agency/logo_url_1774836185_14b54735.png",
  profile_image_url:
    "https://realestate-reports-assets.s3.us-west-2.amazonaws.com/agencies/demo-agency/profile-image.jpg",
};
const AGENT_COLOUR = DEMO_AGENCY.agent_colour;

const SEVIRA = {
  obfuscated_id: "vl3272ep",
  street_number: "22",
  street_name: "Sevira",
  street_type_long: "Avenue",
  suburb: "Gledswood Hills",
  state: "NSW",
  postcode: "2557",
  bedrooms: 7,
  bathrooms: 5,
  carspaces: 2,
  listing_property_type: "House",
  land_area: 496,
  profile_slug: "22-sevira-avenue-gledswood-hills-nsw-2557",
  photo_url_full:
    "https://b.domainstatic.com.au/2020434661_1_1_251119_021840-w1600-h1200",
};

const SEVIRA_DEMO_URL = `https://homeprice.site/property-profile/${SEVIRA.profile_slug}-${SEVIRA.obfuscated_id}?agent=${DEMO_AGENCY.landing_page_slug}`;

/* Comparable sales used in the sliding-in animation */
const COMPARABLES = [
  {
    addr: "18 Sevira Avenue",
    suburb: "Gledswood Hills NSW",
    beds: 6,
    baths: 5,
    cars: 2,
    land: 510,
    sold: 2_580_000,
    days: 14,
    photo:
      "https://b.domainstatic.com.au/2020434661_1_1_251119_021840-w1600-h1200",
  },
  {
    addr: "9 Heritage Way",
    suburb: "Gledswood Hills NSW",
    beds: 7,
    baths: 5,
    cars: 3,
    land: 488,
    sold: 2_695_000,
    days: 28,
    photo:
      "https://b.domainstatic.com.au/2020434661_1_1_251119_021840-w1600-h1200",
  },
  {
    addr: "31 Ridgeline Drive",
    suburb: "Gledswood Hills NSW",
    beds: 6,
    baths: 4,
    cars: 2,
    land: 502,
    sold: 2_510_000,
    days: 41,
    photo:
      "https://b.domainstatic.com.au/2020434661_1_1_251119_021840-w1600-h1200",
  },
  {
    addr: "47 Banks Avenue",
    suburb: "Gledswood Hills NSW",
    beds: 7,
    baths: 5,
    cars: 2,
    land: 495,
    sold: 2_640_000,
    days: 9,
    photo:
      "https://b.domainstatic.com.au/2020434661_1_1_251119_021840-w1600-h1200",
  },
];

/* Address typeahead options */
const TYPEAHEAD_TARGET = "22 Sevira Avenue, Gledswood";
const TYPEAHEAD_RESULTS = [
  "22 Sevira Avenue, Gledswood Hills NSW 2557",
  "18 Sevira Avenue, Gledswood Hills NSW 2557",
  "29 Sevira Avenue, Gledswood Hills NSW 2557",
  "5 Sevira Avenue, Gledswood Hills NSW 2557",
];

/* ─────────── Animations ─────────── */
function GlobalAnimations() {
  return (
    <style>{`
      @keyframes fadeUp {
        0%   { opacity: 0; transform: translateY(10px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeIn {
        0% { opacity: 0; } 100% { opacity: 1; }
      }
      @keyframes slideInRight {
        0%   { opacity: 0; transform: translateX(40px); }
        100% { opacity: 1; transform: translateX(0); }
      }
      @keyframes slideInLeft {
        0%   { opacity: 0; transform: translateX(-40px); }
        100% { opacity: 1; transform: translateX(0); }
      }
      @keyframes slideInDown {
        0%   { opacity: 0; transform: translateY(-16px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes blink {
        0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; }
      }
      @keyframes pulseDot {
        0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.55); }
        50%      { box-shadow: 0 0 0 10px rgba(16,185,129,0); }
      }
      @keyframes rangeFill {
        0%   { width: 0%; }
        100% { width: 100%; }
      }
      @keyframes markerSlide {
        0%   { left: 0%; opacity: 0; }
        100% { opacity: 1; }
      }
      @keyframes shimmer {
        0%   { background-position: -400px 0; }
        100% { background-position: 400px 0; }
      }
      @keyframes formPop {
        0%   { transform: scale(0.92); opacity: 0; }
        60%  { transform: scale(1.02); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes tickIn {
        0%   { transform: scale(0); }
        60%  { transform: scale(1.25); }
        100% { transform: scale(1); }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50%      { transform: translateY(-6px); }
      }
      @keyframes banner {
        0%   { transform: translateY(-100%); }
        100% { transform: translateY(0); }
      }
      @keyframes pingRing {
        0%   { transform: scale(0.7); opacity: 0.8; }
        100% { transform: scale(2.4); opacity: 0; }
      }
      @keyframes typeCaret {
        0%, 100% { opacity: 1; } 50% { opacity: 0; }
      }
      @keyframes notifSlide {
        0%   { transform: translateY(120%); opacity: 0; }
        100% { transform: translateY(0); opacity: 1; }
      }
      @keyframes drawer {
        0%   { transform: translateY(110%); }
        100% { transform: translateY(0); }
      }
    `}</style>
  );
}

/* ─────────── Helpers ─────────── */
function formatMoney(n) {
  return "$" + Math.round(n).toLocaleString("en-AU");
}
function formatMoneyShort(n) {
  if (n >= 1_000_000) return "$" + (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return "$" + Math.round(n / 1_000) + "k";
  return "$" + Math.round(n);
}

function useInView(ref, threshold = 0.25) {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current || seen) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setSeen(true);
      },
      { threshold }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold, seen]);
  return seen;
}

/* ─────────── Counting number ─────────── */
function Counter({ to, prefix = "", suffix = "", decimals = 0, duration = 1600, start }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to, duration, start]);
  const formatted =
    decimals > 0
      ? val.toFixed(decimals)
      : Math.round(val).toLocaleString("en-AU");
  return (
    <span>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

/* ─────────── Agent attribution banner ─────────── */
function AgentAttributionBanner({ compact }) {
  return (
    <div
      style={{
        background: AGENT_COLOUR,
        color: "#fff",
        padding: compact ? "8px 12px" : "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        justifyContent: "space-between",
        animation: "banner 0.6s cubic-bezier(0.2,0.9,0.3,1.2) both",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <img
          src={DEMO_AGENCY.logo_url}
          alt="Demo Agency"
          style={{
            maxHeight: compact ? 20 : 24,
            maxWidth: 80,
            objectFit: "contain",
            filter: "brightness(0) invert(1)",
            flexShrink: 0,
          }}
        />
        <div
          style={{
            fontSize: compact ? 9 : 10,
            fontWeight: 800,
            letterSpacing: 1.2,
            color: "rgba(255,255,255,0.85)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          POWERED BY {DEMO_AGENCY.agency_name.toUpperCase()}
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "rgba(255,255,255,0.12)",
          borderRadius: 999,
          padding: "3px 9px 3px 4px",
          flexShrink: 0,
        }}
      >
        <img
          src={DEMO_AGENCY.profile_image_url}
          alt={DEMO_AGENCY.agent_name}
          style={{
            width: compact ? 20 : 24,
            height: compact ? 20 : 24,
            borderRadius: "50%",
            objectFit: "cover",
            background: "#e5e7eb",
          }}
        />
        <span style={{ fontSize: compact ? 10 : 11, fontWeight: 700 }}>
          {DEMO_AGENCY.agent_first_name}
        </span>
      </div>
    </div>
  );
}

/* ─────────── Hero: Animated browser with full valuator demo ─────────── */
function HeroValuatorDemo({ isMobile }) {
  const [phase, setPhase] = useState("type"); // type → suggest → loading → result
  const [typed, setTyped] = useState("");
  const [tickValue, setTickValue] = useState(0);
  const target = TYPEAHEAD_TARGET;
  const FINAL_VALUE = 2_625_000;
  const LOW = 2_510_000;
  const HIGH = 2_740_000;

  useEffect(() => {
    let cancelled = false;

    async function run() {
      while (!cancelled) {
        // PHASE 1 - type
        setPhase("type");
        setTyped("");
        setTickValue(0);
        for (let i = 1; i <= target.length; i++) {
          if (cancelled) return;
          await new Promise((r) => setTimeout(r, 55));
          setTyped(target.slice(0, i));
        }
        // PHASE 2 - suggestions visible
        setPhase("suggest");
        await new Promise((r) => setTimeout(r, 1300));
        if (cancelled) return;
        // PHASE 3 - loading
        setPhase("loading");
        await new Promise((r) => setTimeout(r, 1400));
        if (cancelled) return;
        // PHASE 4 - result, count up
        setPhase("result");
        const t0 = performance.now();
        const dur = 1500;
        await new Promise((resolve) => {
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setTickValue(FINAL_VALUE * eased);
            if (p < 1 && !cancelled) requestAnimationFrame(tick);
            else resolve();
          };
          requestAnimationFrame(tick);
        });
        await new Promise((r) => setTimeout(r, 4200));
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const showSuggest = phase === "suggest";
  const showLoad = phase === "loading";
  const showResult = phase === "result";

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 30px 80px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.2)",
        border: "1px solid rgba(255,255,255,0.1)",
        width: "100%",
      }}
    >
      {/* Browser chrome */}
      <div
        style={{
          background: "#f1f5f9",
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          gap: 6,
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fca5a5" }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#fcd34d" }} />
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#86efac" }} />
        <div
          style={{
            marginLeft: 10,
            flex: 1,
            minWidth: 0,
            background: "#fff",
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 10.5,
            color: "#64748b",
            border: "1px solid #e2e8f0",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          homeprice.site/demo-agency
        </div>
      </div>

      {/* Agency banner */}
      <AgentAttributionBanner compact />

      {/* Search bar + phase content (min-height locks the card so it doesn't jump between phases) */}
      <div
        style={{
          padding: isMobile ? "14px 14px 14px" : "18px 22px 18px",
          minHeight: isMobile ? 520 : 560,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 1.2,
            color: BRAND_BLUE,
            marginBottom: 6,
          }}
        >
          WHAT'S MY HOME WORTH?
        </div>
        <div
          style={{
            position: "relative",
            border: `1.5px solid ${BRAND_BLUE}`,
            borderRadius: 10,
            padding: "10px 14px 10px 38px",
            background: "#fff",
            fontSize: 14,
            color: "#0f172a",
            boxShadow: `0 0 0 4px ${BRAND_BLUE}22`,
            fontWeight: 500,
            minHeight: 22,
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 13,
              top: "50%",
              transform: "translateY(-50%)",
              color: BRAND_BLUE,
              fontSize: 15,
            }}
          >
            🔍
          </span>
          <span>{typed}</span>
          {(phase === "type" || phase === "suggest") && (
            <span
              style={{
                display: "inline-block",
                width: 1.5,
                height: 16,
                background: BRAND_BLUE,
                verticalAlign: "middle",
                marginLeft: 2,
                animation: "typeCaret 0.9s steps(2) infinite",
              }}
            />
          )}
        </div>

        {/* Typeahead suggestions */}
        {showSuggest && (
          <div
            style={{
              marginTop: 6,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              boxShadow: "0 14px 36px rgba(15,23,42,0.12)",
              overflow: "hidden",
              animation: "slideInDown 0.3s ease both",
            }}
          >
            {TYPEAHEAD_RESULTS.map((s, i) => (
              <div
                key={s}
                style={{
                  padding: "9px 12px",
                  fontSize: 12.5,
                  borderBottom:
                    i < TYPEAHEAD_RESULTS.length - 1 ? "1px solid #f3f4f6" : "none",
                  color: "#111827",
                  background: i === 0 ? "#eff6ff" : "#fff",
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  animation: `slideInDown 0.3s ease ${i * 0.05}s both`,
                }}
              >
                <span style={{ color: BRAND_BLUE }}>📍</span>
                <span
                  style={{
                    fontWeight: i === 0 ? 700 : 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {s}
                </span>
                {i === 0 && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 10,
                      color: BRAND_BLUE,
                      fontWeight: 700,
                    }}
                  >
                    ↵
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Loading skeleton */}
        {showLoad && (
          <div
            style={{
              marginTop: 12,
              padding: "16px 14px",
              background: "#f8fafc",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              animation: "fadeIn 0.3s ease both",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: BRAND_BLUE,
                fontWeight: 700,
                fontSize: 12,
                marginBottom: 10,
              }}
            >
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  border: `2px solid ${BRAND_BLUE}`,
                  borderTopColor: "transparent",
                  display: "inline-block",
                  animation: "fadeIn 0.2s, spin 0.9s linear infinite",
                }}
              />
              <span>AI analysing comparable sales…</span>
            </div>
            {[80, 65, 75].map((w, i) => (
              <div
                key={i}
                style={{
                  height: 10,
                  width: `${w}%`,
                  borderRadius: 4,
                  marginBottom: 7,
                  background:
                    "linear-gradient(90deg, #e2e8f0 0px, #f1f5f9 200px, #e2e8f0 400px)",
                  backgroundSize: "800px 100%",
                  animation: "shimmer 1.2s linear infinite",
                }}
              />
            ))}
          </div>
        )}

        {/* RESULT */}
        {showResult && (
          <div
            style={{
              marginTop: 12,
              animation: "fadeUp 0.5s ease both",
            }}
          >
            {/* Property card */}
            <div
              style={{
                display: "flex",
                gap: 12,
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: 10,
                alignItems: "center",
              }}
            >
              <img
                src={SEVIRA.photo_url_full}
                alt="22 Sevira"
                style={{
                  width: 80,
                  height: 64,
                  borderRadius: 8,
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: "#0f172a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  22 Sevira Avenue
                </div>
                <div style={{ fontSize: 10.5, color: "#64748b", letterSpacing: 0.4 }}>
                  GLEDSWOOD HILLS NSW 2557
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: BRAND_BLUE,
                    fontWeight: 700,
                    marginTop: 3,
                  }}
                >
                  {SEVIRA.bedrooms} bed · {SEVIRA.bathrooms} bath · {SEVIRA.carspaces} car · {SEVIRA.land_area} m²
                </div>
              </div>
            </div>

            {/* Estimate */}
            <div
              style={{
                marginTop: 12,
                background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
                borderRadius: 12,
                padding: "16px 16px 18px",
                color: "#fff",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: 1.4,
                  color: BRAND_ACCENT_COLOR,
                  marginBottom: 4,
                }}
              >
                ESTIMATED VALUE
              </div>
              <div
                style={{
                  fontSize: isMobile ? 28 : 34,
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                }}
              >
                {formatMoney(tickValue)}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.65)",
                  marginTop: 2,
                }}
              >
                Likely range {formatMoneyShort(LOW)} – {formatMoneyShort(HIGH)}
              </div>

              {/* Range bar with vertically-centered midpoint marker */}
              <div
                style={{
                  marginTop: 18,
                  position: "relative",
                  height: 10,
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: 999,
                    background: "rgba(255,255,255,0.12)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: "100%",
                      background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_ACCENT_COLOR})`,
                      animation: "rangeFill 1.4s ease-out both",
                      borderRadius: 999,
                    }}
                  />
                </div>
                {/* marker - vertically centered on the bar's midline */}
                <div
                  style={{
                    position: "absolute",
                    left: "52%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    background: "#fff",
                    border: `3px solid ${BRAND_ACCENT_COLOR}`,
                    boxShadow: "0 0 0 4px rgba(16,185,129,0.25)",
                    animation: "markerSlide 1.4s ease-out both",
                  }}
                />
              </div>
              <div
                style={{
                  marginTop: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 9,
                  color: "rgba(255,255,255,0.55)",
                  fontWeight: 600,
                  letterSpacing: 0.5,
                }}
              >
                <span>{formatMoneyShort(LOW)}</span>
                <span>{formatMoneyShort(HIGH)}</span>
              </div>

              <div
                style={{
                  marginTop: 10,
                  fontSize: 10.5,
                  color: "rgba(255,255,255,0.7)",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: BRAND_ACCENT_COLOR,
                    animation: "pulseDot 1.6s ease-in-out infinite",
                  }}
                />
                Based on 4 recent comparable sales within 800m
              </div>
            </div>

            {/* Comparable sliding in */}
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: "#64748b",
                  letterSpacing: 1,
                  marginBottom: 6,
                }}
              >
                NEARBY COMPARABLE SOLD
              </div>
              {COMPARABLES.slice(0, 2).map((c, i) => (
                <div
                  key={c.addr}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 10px",
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    marginBottom: 6,
                    animation: `slideInRight 0.5s ease ${0.3 + i * 0.2}s both`,
                  }}
                >
                  <img
                    src={c.photo}
                    alt={c.addr}
                    style={{
                      width: 44,
                      height: 36,
                      borderRadius: 6,
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: "#0f172a",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {c.addr}
                    </div>
                    <div style={{ fontSize: 10, color: "#64748b" }}>
                      {c.beds} bed · {c.baths} bath · {c.land} m²
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 800,
                        color: BRAND_ACCENT_COLOR,
                      }}
                    >
                      {formatMoneyShort(c.sold)}
                    </div>
                    <div style={{ fontSize: 9.5, color: "#94a3b8" }}>
                      sold {c.days}d ago
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

/* ─────────── Customer-provided data animation ─────────── */
function CustomerDataDemo({ isMobile }) {
  const [step, setStep] = useState(0); // 0 details, 1 description, 2 image, 3 exclude, 4 refined
  const [typed, setTyped] = useState("");
  const [bedrooms, setBedrooms] = useState(6);
  const [tickValue, setTickValue] = useState(2_625_000);
  const description = "Renovated kitchen, new ducted aircon, north-facing pool.";

  useEffect(() => {
    let cancelled = false;
    async function run() {
      while (!cancelled) {
        setStep(0);
        setBedrooms(6);
        setTyped("");
        setTickValue(2_625_000);
        await new Promise((r) => setTimeout(r, 800));
        if (cancelled) return;
        // bump bedrooms 6 -> 7
        setBedrooms(7);
        await new Promise((r) => setTimeout(r, 1000));
        if (cancelled) return;
        // type description
        setStep(1);
        for (let i = 1; i <= description.length; i++) {
          if (cancelled) return;
          await new Promise((r) => setTimeout(r, 28));
          setTyped(description.slice(0, i));
        }
        await new Promise((r) => setTimeout(r, 700));
        if (cancelled) return;
        // upload image
        setStep(2);
        await new Promise((r) => setTimeout(r, 1300));
        if (cancelled) return;
        // exclude a comparable
        setStep(3);
        await new Promise((r) => setTimeout(r, 1300));
        if (cancelled) return;
        // refined estimate ticks up
        setStep(4);
        const t0 = performance.now();
        const dur = 1200;
        const startVal = 2_625_000;
        const endVal = 2_725_000;
        await new Promise((resolve) => {
          const tick = (t) => {
            const p = Math.min(1, (t - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setTickValue(startVal + (endVal - startVal) * eased);
            if (p < 1 && !cancelled) requestAnimationFrame(tick);
            else resolve();
          };
          requestAnimationFrame(tick);
        });
        await new Promise((r) => setTimeout(r, 2400));
      }
    }
    run();
    return () => { cancelled = true; };
  }, []);

  const detailChips = [
    { name: "bed", value: bedrooms, label: "Bed", bumped: bedrooms === 7 },
    { name: "bath", value: 5, label: "Bath" },
    { name: "car", value: 2, label: "Car" },
    { name: "ruler", value: "496 m²", label: "Land" },
  ];

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        padding: isMobile ? "1.25rem" : "1.75rem",
        boxShadow: "0 14px 36px rgba(15,23,42,0.06)",
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: isMobile ? 18 : 28,
        alignItems: "stretch",
      }}
    >
      {/* Left: customer input panel */}
      <div
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: 14,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: BRAND_BLUE }}>
          CUSTOMER ADDS THEIR OWN DETAILS
        </div>
        {/* Detail chips */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
          {detailChips.map((c) => (
            <div
              key={c.label}
              style={{
                background: c.bumped ? `${BRAND_ACCENT_COLOR}15` : "#fff",
                border: `1px solid ${c.bumped ? BRAND_ACCENT_COLOR : "#e2e8f0"}`,
                borderRadius: 8,
                padding: "6px 4px",
                textAlign: "center",
                transition: "all 0.4s",
              }}
            >
              <Icon name={c.name} size={14} color={c.bumped ? BRAND_ACCENT_COLOR : "#64748b"} />
              <div style={{ fontSize: 13, fontWeight: 800, color: c.bumped ? BRAND_ACCENT_COLOR : "#0f172a", marginTop: 2 }}>
                {c.value}
              </div>
              <div style={{ fontSize: 9, color: "#64748b", fontWeight: 600, letterSpacing: 0.4 }}>
                {c.label}
              </div>
            </div>
          ))}
        </div>
        {/* Description box */}
        <div
          style={{
            background: "#fff",
            border: `1px solid ${step >= 1 ? BRAND_BLUE : "#e2e8f0"}`,
            borderRadius: 8,
            padding: "8px 10px",
            minHeight: 52,
            fontSize: 12,
            color: "#0f172a",
            lineHeight: 1.5,
            transition: "border-color 0.3s",
          }}
        >
          <div style={{ fontSize: 9, color: "#64748b", fontWeight: 700, letterSpacing: 0.4, marginBottom: 3 }}>
            DESCRIPTION
          </div>
          {typed || (
            <span style={{ color: "#94a3b8", fontStyle: "italic" }}>
              Anything special about your home?
            </span>
          )}
          {step === 1 && typed.length < description.length && (
            <span
              style={{
                display: "inline-block",
                width: 1.5,
                height: 12,
                background: BRAND_BLUE,
                marginLeft: 2,
                verticalAlign: "middle",
                animation: "typeCaret 0.9s steps(2) infinite",
              }}
            />
          )}
        </div>
        {/* Image upload */}
        <div
          style={{
            border: `1.5px dashed ${step >= 2 ? BRAND_ACCENT_COLOR : "#cbd5e1"}`,
            borderRadius: 8,
            padding: 10,
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: step >= 2 ? `${BRAND_ACCENT_COLOR}0d` : "#fff",
            transition: "all 0.3s",
          }}
        >
          {step >= 2 ? (
            <>
              <div
                style={{
                  width: 44,
                  height: 36,
                  borderRadius: 6,
                  background: `linear-gradient(135deg, ${BRAND_BLUE}, ${BRAND_ACCENT_COLOR})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  animation: "fadeIn 0.4s ease both",
                }}
              >
                <Icon name="image" size={18} color="#fff" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>kitchen.jpg</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>Sent to {DEMO_AGENCY.agent_first_name}</div>
              </div>
              <Icon name="check" size={16} color={BRAND_ACCENT_COLOR} />
            </>
          ) : (
            <>
              <Icon name="upload" size={18} color="#94a3b8" />
              <div style={{ fontSize: 12, color: "#64748b" }}>Upload photos</div>
            </>
          )}
        </div>
        {/* Exclude comparable */}
        <div>
          <div style={{ fontSize: 9, color: "#64748b", fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}>
            EXCLUDE COMPARABLES THE AI GOT WRONG
          </div>
          {COMPARABLES.slice(0, 2).map((c, i) => {
            const excluded = step >= 3 && i === 0;
            return (
              <div
                key={c.addr}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 8px",
                  background: excluded ? "#fef2f2" : "#fff",
                  border: `1px solid ${excluded ? "#fca5a5" : "#e2e8f0"}`,
                  borderRadius: 6,
                  marginBottom: 4,
                  opacity: excluded ? 0.7 : 1,
                  textDecoration: excluded ? "line-through" : "none",
                  transition: "all 0.4s",
                }}
              >
                <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: "#0f172a", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {c.addr}
                </div>
                <div style={{ fontSize: 11, color: excluded ? "#dc2626" : BRAND_ACCENT_COLOR, fontWeight: 700 }}>
                  {formatMoneyShort(c.sold)}
                </div>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 6,
                    background: excluded ? "#dc2626" : "#f1f5f9",
                    color: excluded ? "#fff" : "#94a3b8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.3s",
                  }}
                >
                  <Icon name="x" size={11} color={excluded ? "#fff" : "#94a3b8"} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: refining estimate */}
      <div
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
          borderRadius: 12,
          padding: 18,
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: 280,
        }}
      >
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, color: BRAND_ACCENT_COLOR, marginBottom: 6 }}>
          REFINED ESTIMATE
        </div>
        <div style={{ fontSize: isMobile ? 28 : 34, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.1 }}>
          {formatMoney(tickValue)}
        </div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 4 }}>
          {step >= 4 ? "Updated with your details" : "Starting estimate"}
        </div>
        <div
          style={{
            marginTop: 14,
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(16,185,129,0.12)",
            border: "1px solid rgba(16,185,129,0.3)",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 11,
            color: BRAND_ACCENT_COLOR,
            fontWeight: 600,
            opacity: step >= 4 ? 1 : 0.3,
            transition: "opacity 0.5s",
          }}
        >
          <Icon name="check" size={14} color={BRAND_ACCENT_COLOR} />
          {step >= 4 ? "+$100k after your input" : "Waiting for details"}
        </div>
        <div style={{ marginTop: 14, fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1.5 }}>
          Every detail the customer adds flows straight to {DEMO_AGENCY.agent_first_name}, giving you context no AVM has.
        </div>
      </div>
    </div>
  );
}

/* ─────────── Review notes animation ─────────── */
function ReviewNotesDemo({ isMobile }) {
  const [phase, setPhase] = useState(0); // 0 prompt, 1 generating, 2 note, 3 notified
  const [typed, setTyped] = useState("");
  const prompt = "estimate looks low, recent reno + great street";
  const note =
    "Local note from " +
    DEMO_AGENCY.agent_first_name +
    ": This estimate is conservative. The kitchen was renovated last year and this pocket of the street has been selling at a 5-8% premium. Happy to walk you through the comps anytime.";

  useEffect(() => {
    let cancelled = false;
    async function run() {
      while (!cancelled) {
        setPhase(0);
        setTyped("");
        for (let i = 1; i <= prompt.length; i++) {
          if (cancelled) return;
          await new Promise((r) => setTimeout(r, 32));
          setTyped(prompt.slice(0, i));
        }
        await new Promise((r) => setTimeout(r, 600));
        if (cancelled) return;
        setPhase(1);
        await new Promise((r) => setTimeout(r, 1200));
        if (cancelled) return;
        setPhase(2);
        await new Promise((r) => setTimeout(r, 1600));
        if (cancelled) return;
        setPhase(3);
        await new Promise((r) => setTimeout(r, 3200));
      }
    }
    run();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: isMobile ? 18 : 28,
        alignItems: "stretch",
      }}
    >
      {/* Agent side */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 14,
          padding: 16,
          boxShadow: "0 12px 28px rgba(15,23,42,0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <IconTile name="edit" size={30} iconSize={14} color="#7c3aed" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>Review note</div>
            <div style={{ fontSize: 10, color: "#64748b" }}>Agent dashboard</div>
          </div>
        </div>
        <div
          style={{
            background: "#faf5ff",
            border: "1px solid #e9d5ff",
            borderRadius: 8,
            padding: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <Icon name="cpu" size={12} color="#7c3aed" />
            <span style={{ fontSize: 11, fontWeight: 700, color: "#7c3aed" }}>Draft with AI</span>
          </div>
          <div
            style={{
              background: "#fff",
              border: "1px solid #d8b4fe",
              borderRadius: 6,
              padding: "8px 10px",
              fontSize: 12,
              color: "#0f172a",
              minHeight: 38,
            }}
          >
            {typed}
            {phase === 0 && typed.length < prompt.length && (
              <span
                style={{
                  display: "inline-block",
                  width: 1.5,
                  height: 11,
                  background: "#7c3aed",
                  marginLeft: 2,
                  verticalAlign: "middle",
                  animation: "typeCaret 0.9s steps(2) infinite",
                }}
              />
            )}
          </div>
        </div>
        {/* Generated note */}
        <div
          style={{
            marginTop: 10,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: 12,
            minHeight: 110,
            position: "relative",
          }}
        >
          {phase === 1 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#7c3aed", fontSize: 12, fontWeight: 600 }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  border: "2px solid #7c3aed",
                  borderTopColor: "transparent",
                  display: "inline-block",
                  animation: "spin 0.9s linear infinite",
                }}
              />
              Generating note…
            </div>
          ) : phase >= 2 ? (
            <div style={{ fontSize: 12, color: "#0f172a", lineHeight: 1.55, animation: "fadeUp 0.4s ease both" }}>
              {note}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
              Your note will appear here.
            </div>
          )}
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
      </div>

      {/* Customer side */}
      <div
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)",
          borderRadius: 14,
          padding: 18,
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, color: BRAND_ACCENT_COLOR }}>
          CUSTOMER RECEIVES
        </div>
        {phase >= 3 ? (
          <div
            style={{
              background: "#fff",
              color: "#0f172a",
              borderRadius: 12,
              padding: 14,
              boxShadow: "0 18px 40px rgba(0,0,0,0.3)",
              animation: "notifSlide 0.5s cubic-bezier(0.2,0.9,0.3,1.2) both",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <img
                src={DEMO_AGENCY.profile_image_url}
                alt={DEMO_AGENCY.agent_name}
                style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }}
              />
              <div>
                <div style={{ fontSize: 12, fontWeight: 800 }}>{DEMO_AGENCY.agent_first_name} left you a note</div>
                <div style={{ fontSize: 10, color: "#64748b" }}>Just now · {DEMO_AGENCY.agency_name}</div>
              </div>
            </div>
            <div style={{ fontSize: 11.5, lineHeight: 1.55, color: "#374151" }}>{note}</div>
          </div>
        ) : (
          <div
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px dashed rgba(255,255,255,0.15)",
              borderRadius: 12,
              padding: 24,
              textAlign: "center",
              fontSize: 12,
              color: "rgba(255,255,255,0.5)",
            }}
          >
            Waiting for {DEMO_AGENCY.agent_first_name}'s note…
          </div>
        )}
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", lineHeight: 1.55 }}>
          The note becomes a soft-touch moment that builds rapport, and the AI uses it in every future monthly update.
        </div>
      </div>
    </div>
  );
}

/* ─────────── Activity tracking animation ─────────── */
function ActivityTrackingDemo({ isMobile }) {
  const events = useMemo(
    () => [
      { icon: "bed", label: "Changed bedrooms", detail: "6 → 7" },
      { icon: "ruler", label: "Updated land size", detail: "496 → 612 m²" },
      { icon: "image", label: "Uploaded photo", detail: "kitchen.jpg" },
      { icon: "eye", label: "Viewed comparable", detail: "9 Heritage Way" },
      { icon: "x", label: "Excluded comparable", detail: "18 Sevira Avenue" },
    ],
    []
  );
  const [feed, setFeed] = useState([{ ...events[0], key: 0 }]);
  const counter = useRef(1);

  useEffect(() => {
    const t = setInterval(() => {
      const next = events[counter.current % events.length];
      counter.current += 1;
      setFeed((prev) => [{ ...next, key: counter.current }, ...prev].slice(0, 5));
    }, 1800);
    return () => clearInterval(t);
  }, [events]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: isMobile ? 18 : 28,
        alignItems: "stretch",
      }}
    >
      {/* Customer phone */}
      <div
        style={{
          background: "#0f172a",
          borderRadius: 14,
          padding: 14,
          border: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          color: "#fff",
        }}
      >
        <div
          style={{
            width: 220,
            background: "#fff",
            color: "#0f172a",
            borderRadius: 18,
            padding: 14,
            boxShadow: "0 20px 50px rgba(0,0,0,0.4)",
          }}
        >
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: BRAND_BLUE, marginBottom: 8 }}>
            YOUR PROPERTY
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}>
            {[
              { name: "bed", v: feed[0]?.icon === "bed" ? "7" : "6", label: "Bed", hot: feed[0]?.icon === "bed" },
              { name: "bath", v: "5", label: "Bath" },
              { name: "car", v: "2", label: "Car" },
              { name: "ruler", v: feed[0]?.icon === "ruler" ? "612" : "496", label: "Land m²", hot: feed[0]?.icon === "ruler" },
            ].map((c) => (
              <div
                key={c.label}
                style={{
                  background: c.hot ? `${BRAND_ACCENT_COLOR}15` : "#f8fafc",
                  border: `1px solid ${c.hot ? BRAND_ACCENT_COLOR : "#e2e8f0"}`,
                  borderRadius: 8,
                  padding: 8,
                  textAlign: "center",
                  transition: "all 0.4s",
                }}
              >
                <Icon name={c.name} size={14} color={c.hot ? BRAND_ACCENT_COLOR : "#64748b"} />
                <div style={{ fontSize: 13, fontWeight: 800, marginTop: 2, color: c.hot ? BRAND_ACCENT_COLOR : "#0f172a" }}>
                  {c.v}
                </div>
                <div style={{ fontSize: 9, color: "#64748b", fontWeight: 600 }}>{c.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 11, color: "rgba(255,255,255,0.55)", textAlign: "center" }}>
          What the customer sees
        </div>
      </div>

      {/* Agent activity feed */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 14,
          padding: 16,
          boxShadow: "0 12px 28px rgba(15,23,42,0.06)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <IconTile name="bell" size={30} iconSize={14} color={BRAND_ACCENT_COLOR} />
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>Activity feed</div>
            <div style={{ fontSize: 10, color: "#64748b" }}>
              {DEMO_AGENCY.agent_first_name}, your CRM
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {feed.map((ev, i) => (
            <div
              key={ev.key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                background: i === 0 ? `${BRAND_ACCENT_COLOR}0d` : "#f8fafc",
                border: `1px solid ${i === 0 ? `${BRAND_ACCENT_COLOR}55` : "#e2e8f0"}`,
                borderRadius: 8,
                animation: i === 0 ? "slideInDown 0.4s ease both" : "none",
                opacity: 1 - i * 0.12,
              }}
            >
              <Icon name={ev.icon} size={14} color={i === 0 ? BRAND_ACCENT_COLOR : "#64748b"} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{ev.label}</div>
                <div style={{ fontSize: 10.5, color: "#64748b" }}>{ev.detail}</div>
              </div>
              {i === 0 && (
                <span style={{ fontSize: 9, fontWeight: 800, color: BRAND_ACCENT_COLOR, letterSpacing: 0.5 }}>
                  LIVE
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────── Animated estimate range explainer ─────────── */
function EstimateRangeBar({ isMobile }) {
  const ref = useRef(null);
  const inView = useInView(ref, 0.3);
  return (
    <div
      ref={ref}
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        padding: isMobile ? "1.5rem" : "2rem",
        boxShadow: "0 14px 36px rgba(15,23,42,0.06)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: 1.4,
          color: BRAND_BLUE,
        }}
      >
        ESTIMATE METHOD
      </div>
      <h3
        style={{
          fontSize: isMobile ? 20 : 24,
          fontWeight: 800,
          color: BRAND_COLOR,
          margin: "4px 0 6px 0",
          letterSpacing: "-0.01em",
        }}
      >
        A real range, not a single fake number.
      </h3>
      <p
        style={{
          fontSize: 14,
          color: GRAY_DARK_COLOR,
          lineHeight: 1.6,
          margin: 0,
        }}
      >
        Buyers see a low, a mid, and a high, calibrated against recent sales.
        It feels real, which is exactly why homeowners trust it enough to hand
        you their details.
      </p>

      <div style={{ marginTop: 28 }}>
        <div
          style={{
            position: "relative",
            height: 14,
            borderRadius: 999,
            background: "#f1f5f9",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: inView ? "100%" : "0%",
              background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_ACCENT_COLOR})`,
              transition: "width 1.6s ease-out",
            }}
          />
        </div>
        <div
          style={{
            position: "relative",
            marginTop: 8,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {[
            { label: "Low", v: "$2.51M", color: BRAND_BLUE },
            { label: "Estimate", v: "$2.63M", color: BRAND_ACCENT_COLOR, bold: true },
            { label: "High", v: "$2.74M", color: BRAND_BLUE },
          ].map((m, i) => (
            <div
              key={m.label}
              style={{
                textAlign: "center",
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(8px)",
                transition: `all 0.6s ease ${0.2 + i * 0.15}s`,
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: m.color,
                  margin: "0 auto 6px",
                  border: m.bold ? "3px solid #fff" : "none",
                  boxShadow: m.bold ? `0 0 0 3px ${m.color}55` : "none",
                }}
              />
              <div
                style={{
                  fontSize: 10,
                  color: "#64748b",
                  letterSpacing: 0.6,
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                {m.label}
              </div>
              <div
                style={{
                  fontSize: m.bold ? 18 : 14,
                  fontWeight: 800,
                  color: BRAND_COLOR,
                  marginTop: 2,
                }}
              >
                {m.v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─────────── Feature pill ─────────── */
function FeaturePill({ icon, title, body, isMobile }) {
  return (
    <div
      style={{
        flex: "1 1 300px",
        maxWidth: 360,
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        padding: isMobile ? "1.5rem" : "1.75rem",
        boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.15s, box-shadow 0.15s",
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 14px 34px rgba(0,0,0,0.12)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.05)";
      }}
    >
      <IconTile name={icon} size={52} iconSize={26} style={{ marginBottom: "1.1rem" }} />
      <h3
        style={{
          margin: "0 0 0.5rem 0",
          fontSize: "1.05rem",
          fontWeight: 700,
          color: BRAND_COLOR,
        }}
      >
        {title}
      </h3>
      <p
        style={{
          margin: 0,
          fontSize: "0.88rem",
          color: GRAY_DARK_COLOR,
          lineHeight: 1.55,
          flex: 1,
        }}
      >
        {body}
      </p>
    </div>
  );
}

/* ─────────── Section header ─────────── */
function SectionHeader({ kicker, title, blurb, light, isMobile }) {
  return (
    <div
      style={{
        textAlign: "center",
        maxWidth: 680,
        margin: "0 auto 2.5rem",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 1.6,
          color: BRAND_ACCENT_COLOR,
          marginBottom: 10,
          textTransform: "uppercase",
        }}
      >
        {kicker}
      </div>
      <h2
        style={{
          fontSize: isMobile ? "1.8rem" : "2.4rem",
          fontWeight: 800,
          color: light ? "#fff" : BRAND_COLOR,
          margin: 0,
          letterSpacing: "-0.02em",
          lineHeight: 1.15,
        }}
      >
        {title}
      </h2>
      {blurb && (
        <p
          style={{
            fontSize: isMobile ? "1rem" : "1.1rem",
            color: light ? "rgba(255,255,255,0.7)" : "#64748b",
            lineHeight: 1.65,
            marginTop: 14,
          }}
        >
          {blurb}
        </p>
      )}
    </div>
  );
}

/* ─────────── MAIN ─────────── */
export default function DIYAIPropertyValuator() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [showContact, setShowContact] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div style={{ fontFamily: "Inter, Arial, sans-serif", background: "#fff" }}>
      <GlobalAnimations />
      <Navbar onSignIn={() => setShowSignIn(true)} />

      {/* ─────────── HERO ─────────── */}
      <header
        style={{
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #134e4a 100%)",
          padding: isMobile ? "3.5rem 1.25rem 4rem" : "5.5rem 2rem 6rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "20%",
            left: "-10%",
            width: 420,
            height: 420,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(16,185,129,0.18), transparent 70%)",
            filter: "blur(20px)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "10%",
            right: "-10%",
            width: 460,
            height: 460,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(59,130,246,0.15), transparent 70%)",
            filter: "blur(20px)",
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            position: "relative",
            zIndex: 1,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            alignItems: "center",
            gap: isMobile ? "2.5rem" : "3rem",
          }}
        >
          <div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "rgba(16,185,129,0.15)",
                border: "1px solid rgba(16,185,129,0.3)",
                color: BRAND_ACCENT_COLOR,
                fontSize: 12.5,
                fontWeight: 700,
                borderRadius: 999,
                padding: "5px 12px",
                marginBottom: 20,
                letterSpacing: 0.4,
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: BRAND_ACCENT_COLOR,
                  animation: "pulseDot 1.6s ease-in-out infinite",
                }}
              />
              DIY AI PROPERTY VALUATOR
            </div>
            <h1
              style={{
                fontSize: isMobile ? "2.1rem" : "3.1rem",
                fontWeight: 800,
                color: "#fff",
                lineHeight: 1.1,
                letterSpacing: "-0.025em",
                margin: 0,
              }}
            >
              Every curious homeowner becomes a{" "}
              <span
                style={{
                  background: `linear-gradient(90deg, ${BRAND_ACCENT_COLOR}, ${BRAND_BLUE})`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                vendor lead.
              </span>
            </h1>
            <p
              style={{
                fontSize: isMobile ? "1rem" : "1.15rem",
                color: "rgba(255,255,255,0.72)",
                lineHeight: 1.65,
                marginTop: 18,
                maxWidth: 520,
              }}
            >
              A free, instant estimate on your branded page. They type their
              address, the AI does the rest, and the lead lands in your inbox
              while they're still scrolling.
            </p>

            <div
              style={{
                marginTop: 28,
                display: "flex",
                gap: 10,
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <button
                onClick={() => setShowSignIn(true)}
                style={{
                  border: "none",
                  borderRadius: 10,
                  padding: "0.95rem 1.8rem",
                  fontSize: "1rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  background: BRAND_ACCENT_COLOR,
                  color: "#fff",
                  boxShadow: "0 8px 24px rgba(16,185,129,0.45)",
                  transition: "transform 0.15s, box-shadow 0.15s",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 12px 28px rgba(16,185,129,0.6)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 24px rgba(16,185,129,0.45)";
                }}
              >
                Get started, it's free
              </button>
              <a
                href={SEVIRA_DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  border: "1px solid rgba(255,255,255,0.25)",
                  borderRadius: 10,
                  padding: "0.95rem 1.8rem",
                  fontSize: "1rem",
                  fontWeight: 700,
                  background: "rgba(255,255,255,0.07)",
                  color: "#fff",
                  textDecoration: "none",
                  textAlign: "center",
                  transition: "background 0.15s",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.background = "rgba(255,255,255,0.07)")
                }
              >
                See it live ↗
              </a>
            </div>

            <div
              style={{
                marginTop: 28,
                display: "flex",
                gap: 18,
                flexWrap: "wrap",
                fontSize: 12,
                color: "rgba(255,255,255,0.55)",
                fontWeight: 600,
              }}
            >
              <span>✓ Branded to your agency</span>
              <span>✓ Embed anywhere</span>
              <span>✓ Lead alerts in real-time</span>
            </div>
          </div>

          <div style={{ minWidth: 0 }}>
            <HeroValuatorDemo isMobile={isMobile} />
          </div>
        </div>
      </header>

      {/* ─────────── SECTION: Customer-provided data ─────────── */}
      <section
        style={{
          padding: isMobile ? "3.5rem 1.25rem" : "5rem 2rem",
          background: "#f8fafc",
        }}
      >
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <SectionHeader
            kicker="BUILT TOGETHER"
            title="The customer fills in what the data misses."
            isMobile={isMobile}
          />
          <CustomerDataDemo isMobile={isMobile} />
          <div
            style={{
              marginTop: 28,
              display: "flex",
              flexWrap: "wrap",
              gap: 18,
              justifyContent: "center",
            }}
          >
            {[
              {
                icon: "bed",
                title: "Bedrooms, bathrooms, car spaces",
                body: "Fill in the basics when the public record is incomplete or wrong.",
              },
              {
                icon: "ruler",
                title: "Land size and property type",
                body: "Correct the lot size and dwelling type so comparables line up properly.",
              },
              {
                icon: "edit",
                title: "Free-text description",
                body: "Renovations, views, aspect, recent works, anything the AI can't see from a photo.",
              },
              {
                icon: "image",
                title: "Photos",
                body: "Upload kitchen, bath, and outdoor shots. They land in your inbox alongside the lead.",
              },
              {
                icon: "x",
                title: "Exclude comparables",
                body: "Tap to remove any comp the customer feels isn't a fair match. The estimate adjusts live.",
              },
            ].map((f) => (
              <FeaturePill key={f.title} isMobile={isMobile} {...f} />
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── SECTION: Review notes ─────────── */}
      <section
        style={{
          padding: isMobile ? "3.5rem 1.25rem" : "5rem 2rem",
          background: "#fff",
        }}
      >
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <SectionHeader
            kicker="REVIEW NOTES"
            title="Leave a local-knowledge note. The customer gets a personal moment."
            isMobile={isMobile}
          />
          <ReviewNotesDemo isMobile={isMobile} />
        </div>
      </section>

      {/* ─────────── SECTION: Activity tracking ─────────── */}
      <section
        style={{
          padding: isMobile ? "3.5rem 1.25rem" : "5rem 2rem",
          background: "#f8fafc",
        }}
      >
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <SectionHeader
            kicker="ACTIVITY TRACKING"
            title="Every change they make pings you in real time."
            isMobile={isMobile}
          />
          <ActivityTrackingDemo isMobile={isMobile} />
        </div>
      </section>

      {/* ─────────── SECTION: Estimate range explainer ─────────── */}
      <section
        style={{
          padding: isMobile ? "3.5rem 1.25rem" : "5rem 2rem",
          background: "#fff",
        }}
      >
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: isMobile ? "2rem" : "3rem",
            alignItems: "center",
          }}
        >
          <EstimateRangeBar isMobile={isMobile} />
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: 1.5,
                color: BRAND_BLUE,
                marginBottom: 10,
              }}
            >
              WHY IT CONVERTS
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.7rem" : "2.2rem",
                fontWeight: 800,
                color: BRAND_COLOR,
                margin: 0,
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}
            >
              It converts because the customer stays in control.
            </h2>
            <p
              style={{
                fontSize: 15,
                color: GRAY_DARK_COLOR,
                lineHeight: 1.7,
                marginTop: 14,
              }}
            >
              No forced sign-up, no contact form blocking the estimate, no agent
              call to dodge. You already know who they are: the unique link you
              sent identifies them silently. So they relax, lean in, and tell
              you more than any lead-gate ever would.
            </p>
            <div
              style={{
                marginTop: 22,
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 10,
              }}
            >
              {[
                ["The customer is in control of the estimate, not gated by it", "sliders"],
                ["No demand for contact info: they're not asked for name, email, or phone", "checkCircle"],
                ["They don't need to talk to an agent to get a real number", "message"],
                ["You still know who completed it: every link is uniquely tied to a contact", "send"],
              ].map(([t, icon]) => (
                <div
                  key={t}
                  style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: "10px 14px",
                  }}
                >
                  <IconTile name={icon} size={30} iconSize={14} />
                  <div
                    style={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: GRAY_DARK_COLOR,
                    }}
                  >
                    {t}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── SECTION: Your brand (compact strip) ─────────── */}
      <section
        style={{
          padding: isMobile ? "2.5rem 1.25rem" : "3rem 2rem",
          background: "#fff",
        }}
      >
        <div
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 18,
            alignItems: "center",
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 14,
            padding: isMobile ? 18 : "18px 24px",
          }}
        >
          <IconTile name="brand" size={44} iconSize={20} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.4, color: BRAND_ACCENT_COLOR, marginBottom: 2 }}>
              YOUR BRAND, NOT OURS
            </div>
            <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 700, color: BRAND_COLOR }}>
              Your logo, colours, and headshot on every page. No Vendorleads watermarks.
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── SECTION: Closing pitch ─────────── */}
      <section
        style={{
          background:
            "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #134e4a 100%)",
          padding: isMobile ? "4rem 1.25rem" : "6rem 2rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            maxWidth: 720,
            margin: "0 auto",
            textAlign: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 1.5,
              color: BRAND_ACCENT_COLOR,
              marginBottom: 12,
            }}
          >
            STOP CHASING LEADS
          </div>
          <h2
            style={{
              fontSize: isMobile ? "2rem" : "2.8rem",
              fontWeight: 800,
              color: "#fff",
              margin: 0,
              lineHeight: 1.15,
              letterSpacing: "-0.025em",
            }}
          >
            Let curious homeowners come to{" "}
            <span style={{ color: BRAND_ACCENT_COLOR }}>you.</span>
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: isMobile ? "1rem" : "1.15rem",
              lineHeight: 1.7,
              marginTop: 16,
            }}
          >
            Set up your branded valuator once. Watch leads flow in 24/7. Pick up
            the phone when someone's actually interested in selling.
          </p>
          <div
            style={{
              marginTop: 28,
              display: "flex",
              gap: 10,
              justifyContent: "center",
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <button
              onClick={() => setShowSignIn(true)}
              style={{
                border: "none",
                borderRadius: 10,
                padding: "1rem 2rem",
                fontSize: "1.05rem",
                fontWeight: 700,
                cursor: "pointer",
                background: BRAND_ACCENT_COLOR,
                color: "#fff",
                boxShadow: "0 10px 28px rgba(16,185,129,0.45)",
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 14px 32px rgba(16,185,129,0.6)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 10px 28px rgba(16,185,129,0.45)";
              }}
            >
              Get started, it's free
            </button>
            <button
              onClick={() => setShowContact(true)}
              style={{
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: 10,
                padding: "1rem 2rem",
                fontSize: "1.05rem",
                fontWeight: 700,
                background: "rgba(255,255,255,0.07)",
                color: "#fff",
                cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.15)")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.background = "rgba(255,255,255,0.07)")
              }
            >
              Talk to us
            </button>
          </div>
        </div>
      </section>

      {showSignIn && (
        <SignInModal show={showSignIn} onHide={() => setShowSignIn(false)} />
      )}
      {showContact && (
        <ContactVendorleadsModal
          show={showContact}
          onHide={() => setShowContact(false)}
        />
      )}
    </div>
  );
}
